import { useState } from 'react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Card, CardContent } from '../ui/card';
import { cn } from '../../lib/utils';
import { Loader2, CheckCircle2, AlertCircle, X, Minimize2, ChevronDown } from 'lucide-react';
import { useBackgroundJobs } from '../../contexts/BackgroundJobsContext';

const statusIcon = (s) => {
  if (s === 'completed') return <CheckCircle2 className="h-3.5 w-3.5 text-green-600" />;
  if (s === 'failed') return <AlertCircle className="h-3.5 w-3.5 text-red-500" />;
  return <Loader2 className="h-3.5 w-3.5 text-slate-500 animate-spin" />;
};

const statusLabel = (s) => {
  if (s === 'completed') return 'Done';
  if (s === 'failed') return 'Failed';
  if (s === 'processing') return 'Running';
  return 'Pending';
};

const BackgroundJobsIndicator = ({ onViewJob }) => {
  const { jobs, activeCount, dismissJob } = useBackgroundJobs();
  const [expanded, setExpanded] = useState(false);

  if (jobs.length === 0) return null;

  return (
    <div className="fixed bottom-4 right-4 z-50" data-testid="bg-jobs-indicator">
      {/* Pill button */}
      <Button
        variant="outline"
        size="sm"
        className={cn(
          "rounded-full shadow-lg border-slate-300 bg-white gap-1.5 h-9 px-3",
          activeCount > 0 && "border-black"
        )}
        onClick={() => setExpanded(v => !v)}
        data-testid="bg-jobs-toggle"
      >
        {activeCount > 0 ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Minimize2 className="h-3.5 w-3.5" />}
        <span className="text-xs font-medium">Jobs</span>
        {activeCount > 0 && (
          <Badge className="h-4 min-w-[16px] text-[10px] px-1 bg-black text-white" data-testid="bg-jobs-count">
            {activeCount}
          </Badge>
        )}
        <ChevronDown className={cn("h-3 w-3 transition-transform", expanded && "rotate-180")} />
      </Button>

      {/* Panel */}
      {expanded && (
        <Card className="absolute bottom-11 right-0 w-80 shadow-xl border-slate-200 max-h-[320px] overflow-y-auto" data-testid="bg-jobs-panel">
          <CardContent className="p-0">
            <div className="px-3 py-2 border-b border-slate-100 flex items-center justify-between">
              <span className="text-xs font-semibold text-slate-700">Background Jobs</span>
              <span className="text-[10px] text-slate-400">{jobs.length} total</span>
            </div>
            {jobs.map((job) => {
              const pct = job.total_rows > 0 ? Math.round((job.processed_rows || 0) / job.total_rows * 100) : 0;
              return (
                <div key={job.job_id} className="px-3 py-2.5 border-b border-slate-50 last:border-b-0" data-testid={`bg-job-${job.job_id}`}>
                  <div className="flex items-center justify-between mb-1">
                    <div className="flex items-center gap-1.5">
                      {statusIcon(job.status)}
                      <span className="text-[11px] font-medium text-slate-700 truncate max-w-[160px]">
                        {job.label || 'Import'}
                      </span>
                    </div>
                    <div className="flex items-center gap-1">
                      <Badge variant="outline" className="text-[9px] px-1 h-4">{statusLabel(job.status)}</Badge>
                      {(job.status === 'completed' || job.status === 'failed') && (
                        <button onClick={() => dismissJob(job.job_id)} className="text-slate-300 hover:text-slate-500" data-testid={`dismiss-job-${job.job_id}`}>
                          <X className="h-3 w-3" />
                        </button>
                      )}
                    </div>
                  </div>
                  {(job.status === 'pending' || job.status === 'processing') && job.total_rows > 0 && (
                    <div className="flex items-center gap-2">
                      <div className="flex-1 bg-slate-100 rounded-full h-1.5">
                        <div className="bg-black rounded-full h-1.5 transition-all" style={{ width: `${pct}%` }} />
                      </div>
                      <span className="text-[9px] text-slate-400 w-8 text-right">{pct}%</span>
                    </div>
                  )}
                  <div className="flex items-center gap-2 mt-1 text-[9px] text-slate-400">
                    {typeof job.imported === 'number' && <span className="text-green-700">{job.imported} imp</span>}
                    {typeof job.duplicates === 'number' && job.duplicates > 0 && <span className="text-amber-600">{job.duplicates} dup</span>}
                    {typeof job.errors === 'number' && job.errors > 0 && <span className="text-red-600">{job.errors} err</span>}
                    <span className="ml-auto">{job.processed_rows || 0}/{job.total_rows || '?'} rows</span>
                  </div>
                  {onViewJob && (
                    <button
                      className="text-[10px] text-slate-500 hover:text-black mt-1 underline"
                      onClick={() => { setExpanded(false); onViewJob(job); }}
                      data-testid={`view-job-${job.job_id}`}
                    >
                      View details
                    </button>
                  )}
                </div>
              );
            })}
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default BackgroundJobsIndicator;
